import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

function StatusBadge({ status }) {
  const map = {
    pending: "bg-amber-500/10 text-amber-400 border-amber-500/20",
    awaiting_proof: "bg-zinc-800 text-zinc-400 border-zinc-700",
    approved: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
    rejected: "bg-red-500/10 text-red-400 border-red-500/20",
  };
  return (
    <span className={`text-[10px] font-mono font-bold px-2 py-0.5 rounded-sm border ${map[status] || "bg-zinc-800"}`}>
      {status.toUpperCase()}
    </span>
  );
}

export default function Transactions() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [type, setType] = useState("all");

  const load = async () => {
    const params = new URLSearchParams({ page_size: "100" });
    if (status !== "all") params.set("status", status);
    if (type !== "all") params.set("type", type);
    if (q) params.set("q", q);
    const r = await api.get(`/transactions?${params.toString()}`);
    setItems(r.data.items);
    setTotal(r.data.total);
  };

  useEffect(() => {
    load();
    const id = setInterval(() => {
      load();
    }, 15000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, type]);

  return (
    <div className="space-y-6 max-w-[1400px]" data-testid="transactions-page">
      <div>
        <div className="overline mb-2">Historial completo</div>
        <h1 className="font-display font-black text-2xl sm:text-3xl lg:text-4xl tracking-tight">Transacciones</h1>
        <p className="text-sm text-zinc-500 mt-2">{total} transacciones registradas.</p>
      </div>

      <div className="flex gap-3 flex-wrap">
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && load()}
          placeholder="Buscar por referencia o ID"
          data-testid="tx-search-input"
          className="bg-zinc-950 border-zinc-800 rounded-sm max-w-xs"
        />
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="bg-zinc-950 border-zinc-800 rounded-sm max-w-[180px]" data-testid="tx-status-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-800 rounded-sm">
            <SelectItem value="all">Todos los estados</SelectItem>
            <SelectItem value="pending">Pendiente</SelectItem>
            <SelectItem value="awaiting_proof">Esperando comprobante</SelectItem>
            <SelectItem value="approved">Aprobado</SelectItem>
            <SelectItem value="rejected">Rechazado</SelectItem>
          </SelectContent>
        </Select>
        <Select value={type} onValueChange={setType}>
          <SelectTrigger className="bg-zinc-950 border-zinc-800 rounded-sm max-w-[180px]" data-testid="tx-type-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-800 rounded-sm">
            <SelectItem value="all">Todos los tipos</SelectItem>
            <SelectItem value="credit">Créditos</SelectItem>
            <SelectItem value="membership">Membresía</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="border border-zinc-800 rounded-sm overflow-hidden bg-zinc-900/50">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-zinc-800 bg-black/40">
                <th className="text-left px-4 py-3 overline">Ref</th>
                <th className="text-left px-4 py-3 overline">Usuario</th>
                <th className="text-left px-4 py-3 overline">Tipo</th>
                <th className="text-left px-4 py-3 overline">Producto</th>
                <th className="text-right px-4 py-3 overline">USD</th>
                <th className="text-right px-4 py-3 overline">MXN</th>
                <th className="text-left px-4 py-3 overline">Estado</th>
                <th className="text-left px-4 py-3 overline">Creada</th>
                <th className="text-left px-4 py-3 overline">Revisada</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr>
                  <td colSpan={9} className="p-10 text-center text-zinc-500">
                    No hay transacciones con estos filtros.
                  </td>
                </tr>
              )}
              {items.map((t) => (
                <tr key={t.id} className="border-b border-zinc-900 hover:bg-zinc-900/40" data-testid={`tx-row-${t.id}`}>
                  <td className="px-4 py-3 font-mono text-xs text-zinc-500">{t.reference}</td>
                  <td className="px-4 py-3">
                    {t.telegram_username ? `@${t.telegram_username}` : ""}
                    <div className="text-[10px] text-zinc-600 font-mono">{t.telegram_id}</div>
                  </td>
                  <td className="px-4 py-3 text-xs uppercase">{t.type}</td>
                  <td className="px-4 py-3">
                    {t.item_name}
                    <div className="text-[10px] text-zinc-500 font-mono">
                      {t.type === "credit" ? `${t.credits_or_days} créd.` : `${t.credits_or_days} días`}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-emerald-400">${t.price_usd}</td>
                  <td className="px-4 py-3 text-right font-mono text-emerald-400">${t.price_mxn}</td>
                  <td className="px-4 py-3"><StatusBadge status={t.status} /></td>
                  <td className="px-4 py-3 text-xs text-zinc-500">
                    {new Date(t.created_at).toLocaleString("es-MX")}
                  </td>
                  <td className="px-4 py-3 text-xs text-zinc-500">
                    {t.reviewed_at ? new Date(t.reviewed_at).toLocaleString("es-MX") : "—"}
                    {t.rejection_reason && (
                      <div className="text-red-400 text-[10px] mt-0.5">({t.rejection_reason})</div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
